/**
 * Admin — gestion des utilisateurs + actions RGPD (bloc 1).
 *
 * Recherche, changement de tier, vérification forcée d'email, demande de
 * suppression (RGPD, grâce 30 j) et export des données. Toutes les mutations
 * passent par l'API admin (gardée par admin_user) et sont auditées côté serveur.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import {
  adminUserExportUrl,
  listAdminUsers,
  requestAdminUserDeletion,
  setAdminUserTier,
  verifyAdminUserEmail,
  type AdminUserListItem,
} from "@/api/admin";

export default function AdminUsersPage() {
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "users", q],
    queryFn: () => listAdminUsers({ q: q || undefined, limit: 100 }),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["admin", "users"] });

  const tierMut = useMutation({
    mutationFn: ({ id, tier }: { id: string; tier: "free" | "premium" }) =>
      setAdminUserTier(id, tier),
    onSuccess: invalidate,
  });
  const verifyMut = useMutation({
    mutationFn: (id: string) => verifyAdminUserEmail(id),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => requestAdminUserDeletion(id),
    onSuccess: invalidate,
  });

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-6 py-12">
        <Link to="/admin" className="text-sm text-blue-900 underline">
          ← Admin
        </Link>
        <h1 className="mt-3 text-2xl font-bold">Utilisateurs</h1>

        <form
          className="mt-4 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(search.trim());
          }}
        >
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par email…"
            className="w-full max-w-sm rounded border border-gray-300 px-3 py-1.5 text-sm"
          />
          <button
            type="submit"
            className="rounded bg-blue-900 px-4 py-1.5 text-sm text-white hover:bg-blue-800"
          >
            Rechercher
          </button>
        </form>

        {isLoading && <p className="mt-6 text-sm text-gray-500">Chargement…</p>}
        {error && (
          <p className="mt-6 text-sm text-red-700">Impossible de charger les utilisateurs.</p>
        )}

        {data && (
          <>
            <p className="mt-4 text-xs text-gray-500">
              {data.total} utilisateur{data.total > 1 ? "s" : ""}
            </p>
            <div className="mt-2 overflow-x-auto rounded border border-gray-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Tier</th>
                    <th className="px-3 py-2">Email vérifié</th>
                    <th className="px-3 py-2">Biens</th>
                    <th className="px-3 py-2">Statut</th>
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((u) => (
                    <UserRow
                      key={u.id}
                      u={u}
                      onSetTier={(tier) => tierMut.mutate({ id: u.id, tier })}
                      onVerify={() => verifyMut.mutate(u.id)}
                      onDelete={() => {
                        if (
                          window.confirm(
                            `Demander la suppression RGPD de ${u.email} ? (grâce 30 jours)`,
                          )
                        ) {
                          deleteMut.mutate(u.id);
                        }
                      }}
                      busy={tierMut.isPending || verifyMut.isPending || deleteMut.isPending}
                    />
                  ))}
                  {data.items.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                        Aucun utilisateur.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

function UserRow({
  u,
  onSetTier,
  onVerify,
  onDelete,
  busy,
}: {
  u: AdminUserListItem;
  onSetTier: (tier: "free" | "premium") => void;
  onVerify: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const pendingDeletion = u.deletion_requested_at != null;
  return (
    <tr className="border-t border-gray-100">
      <td className="px-3 py-2">
        {u.email}
        {u.is_admin && (
          <span className="ml-2 rounded bg-blue-100 px-1.5 text-xs font-semibold text-blue-800">
            admin
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <select
          value={u.tier}
          disabled={busy}
          onChange={(e) => onSetTier(e.target.value as "free" | "premium")}
          className="rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="free">free</option>
          <option value="premium">premium</option>
        </select>
      </td>
      <td className="px-3 py-2">
        {u.email_verified ? (
          <span className="text-green-700">oui</span>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={onVerify}
            className="rounded border border-gray-300 px-2 py-0.5 text-xs hover:bg-gray-100"
          >
            Vérifier
          </button>
        )}
      </td>
      <td className="px-3 py-2">{u.logements_count}</td>
      <td className="px-3 py-2">
        {pendingDeletion ? (
          <span className="rounded bg-red-100 px-1.5 text-xs font-semibold text-red-700">
            suppression en attente
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-2">
          <a
            href={adminUserExportUrl(u.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded border border-gray-300 px-2 py-0.5 text-xs hover:bg-gray-100"
          >
            Exporter
          </a>
          {!pendingDeletion && !u.is_admin && (
            <button
              type="button"
              disabled={busy}
              onClick={onDelete}
              className="rounded border border-red-300 px-2 py-0.5 text-xs text-red-700 hover:bg-red-50"
            >
              Supprimer
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
